import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';
import { useTheme } from '@mui/material/styles';

import SearchIcon from '@mui/icons-material/Search';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import type { AppDispatch } from '../../../store/store';
import { CompanySearchResult } from '../../../types/company';
import { resetSelectedCompany, searchCompanyData, selectCompany } from './slice';
import { useIntl } from 'react-intl';

const CompanySearch = ({ onChange }: { onChange: (value: CompanySearchResult) => void }) => {
	const theme = useTheme();
	const intl = useIntl();
	const dispatch = useDispatch<AppDispatch>();
	const companySearch = useSelector((state: any) => state.companySearch);

	const loading = companySearch?.loading;
	const companies = companySearch?.companies;
	const isOptionBusinessId = companySearch?.isOptionBusinessId;

	const [value, setValue] = useState('');
	const [debouncedValue] = useDebounce(value, 500);

	useEffect(() => {
		if (debouncedValue.length > 2) {
			dispatch(searchCompanyData(debouncedValue));
		} else {
			dispatch(resetSelectedCompany());
		}
	}, [debouncedValue, dispatch]);

	const onSelectCompany = async (_: any, value: string | CompanySearchResult) => {
		if (typeof value === 'string') return;

		await dispatch(selectCompany(value));
		onChange(value);
	};

	const getOptionLabel = (option: string | CompanySearchResult) => {
		if (typeof option === 'string') {
			return option;
		}
		if (isOptionBusinessId) {
			return option?.businessId ?? option;
		} else {
			return option?.name ?? option;
		}
	};

	const onChangeName = (event: any) => setValue(event.target.value);

	const options = companies ?? [];

	const sortedOptions = [...options]
		.filter(option => option.businessId !== null)
		.sort((a, b) => b.isCustomer - a.isCustomer)
		.filter(
			(option, index, self) => index === self.findIndex(t => t.companyId === option.companyId && t.businessId === option.businessId),
		)
		.slice(0, 50);

	const simplifiedOptions = sortedOptions
		.map(option => ({
			key: `${option.businessId}-${option.companyId}`,
			name: option.name,
			businessId: option.businessId,
			companyId: option.companyId,
		}))
		.sort((a, b) => a.key.localeCompare(b.key));

	const renderInput = (params: any) => (
		<TextField
			variant='outlined'
			placeholder={intl.formatMessage({ id: 'companySearch.searchPlaceholder' })}
			onChange={onChangeName}
			size='small'
			{...params}
			InputProps={{
				...params.InputProps,
				startAdornment: (
					<InputAdornment position='start'>
						<SearchIcon color='primary' fontSize='small' />
					</InputAdornment>
				),
				endAdornment: (
					<>
						{loading ? <CircularProgress color='inherit' size={16} /> : null}
						{params.InputProps?.endAdornment}
					</>
				),
			}}
		/>
	);

	const renderOption = (props: any, option: CompanySearchResult) => (
		<ListItem
			key={`${option.businessId}-${option.companyId}`}
			alignItems='center'
			{...props}
			sx={{
				py: 0.5,
				'&:hover': {
					backgroundColor: theme.palette.action.hover,
				},
			}}
		>
			<Grid container>
				<Grid item>
					<ListItemText
						primary={
							<Typography component='span' color='primary' variant='body2'>
								{option.name} - {option.companyId}
							</Typography>
						}
					/>
				</Grid>
			</Grid>
		</ListItem>
	);

	return (
		<Autocomplete
			freeSolo
			fullWidth
			disableClearable
			clearOnEscape
			getOptionLabel={getOptionLabel}
			options={sortedOptions}
			value={value}
			onChange={onSelectCompany}
			loading={loading}
			renderInput={renderInput}
			renderOption={renderOption}
			size='small'
		/>
	);
};

export default CompanySearch;
